import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AuthMe,
  CalendarAvailableRoom,
  CalendarIntegration,
  ChatIntegration,
} from '@cornerstone/shared';
import { GOOGLE_SCOPE } from '@cornerstone/shared';
import { API_BASE } from '../../client/config';
import { PageHeader } from '../../nav/Breadcrumb';
import { ConfirmModal } from '../../components/menu';
import { Button } from '../../components/ui';
import { describeApiError, toast } from '../../components/toast';
import { cn } from '../../utils/cn';
import { AppIntegrationsSkeleton } from '../../components/skeleton/layouts/AppIntegrationsSkeleton';
import googleChatIcon from '../../assets/google-chat.svg';
import googleCalendarIcon from '../../assets/google-calendar.svg';

// =============================================================================
// H9 > 앱 연동 (Figma 2-pane) — 좌: 연동 목록(연동됨/연동되지 않음),
// 우: 앱 정보(안내·연동 토글·권한) + 연동 정보(계정/스페이스/상태/연동일).
//
// 데이터 모델은 기존 그대로(스페이스별 ChatIntegration row / 회의실별
// CalendarIntegration row) — 이 화면이 조직 단위 UX로 묶어서 보여준다.
//   · 연동됨 = enabled row 존재. 토글 OFF = 전 row enabled=false(데이터 유지).
//   · 수신 스페이스 칩 = chat rows. 추가=POST, X=DELETE.
//   · 승인 스페이스(최대 1) = 모든 chat rows의 approvalSpaceIds로 일괄 반영.
//   · Calendar '확인' = 계정의 회의실 캘린더 전체 자동 등록(회의실 템플릿 파이프라인).
// =============================================================================

type AppKey = 'chat' | 'calendar';

type Space = { name: string; displayName: string };

// 실패 응답에서 서버 안내 메시지(error)를 꺼내 토스트에 그대로 노출한다.
async function apiErrorMessage(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  return body?.error ?? `HTTP ${res.status}`;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}. ${p(d.getMonth() + 1)}. ${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

const APP_META: Record<AppKey, { title: string; icon: JSX.Element; intro: JSX.Element; perms: Array<[string, string]> }> = {
  chat: {
    title: 'Google Chat',
    icon: <ChatIcon />,
    intro: (
      <>
        Google Chat을 연결하면 지정한 스페이스의 메시지를 자동으로 읽고 미디어 콘텐츠로 변환합니다.
        <br />
        활성화하면 메시지가 들어올 때마다 AI기반의 공지 초안이 생성되며, 사용자 승인 이후 미디어로 게시됩니다.
        <br />
        <br />이 앱 연동은 조직이 공유합니다.
      </>
    ),
    perms: [
      ['Google Chat 메시지 읽기', '지정한 스페이스의 공지 메시지를 수신해 초안을 생성합니다.'],
      ['Google Chat 메시지 작성하기', '지정된 스페이스에 승인 목록을 제시하고 사용자의 확정을 처리합니다.'],
      ['Google Chat 스페이스 목록 읽기', 'HNINE 공지를 받거나 송출할 스페이스를 검색 · 선택합니다.'],
    ],
  },
  calendar: {
    title: 'Google Calendar',
    icon: <CalendarIcon />,
    intro: (
      <>
        Google Calendar를 연결하면 회의실 일정을 읽고 미디어 템플릿에 실시간으로 보여줄 수 있습니다.
        <br />
        활성화되면 [미디어 관리 &gt; 라이브러리]에 각 회의실 명의 템플릿이 생성되며, 등록한 회의실 일정을
        60초마다 동기화합니다.
        <br />
        <br />이 앱 연동은 조직이 공유합니다.
      </>
    ),
    perms: [
      ['Google Calendar 일정 읽기', '등록한 회의실의 일정을 주기적으로 읽습니다. 일정은 화면 표시 용도로만 사용되며, 직접 수정하지 않습니다.'],
      ['회의실 목록 읽기', '조직의 회의실 정보를 읽고 검색 · 등록합니다.'],
    ],
  },
};

export function AppIntegrations(): JSX.Element {
  const [selected, setSelected] = useState<AppKey>('chat');
  const [me, setMe] = useState<AuthMe | null>(null);
  const [chatRows, setChatRows] = useState<ChatIntegration[]>([]);
  const [calRows, setCalRows] = useState<CalendarIntegration[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [meR, cR, kR] = await Promise.all([
        fetch(`${API_BASE}/api/auth/me`),
        fetch(`${API_BASE}/api/integrations/chat`),
        fetch(`${API_BASE}/api/integrations/calendar`),
      ]);
      if (meR.ok) setMe((await meR.json()) as AuthMe);
      if (cR.ok) setChatRows((await cR.json()) as ChatIntegration[]);
      if (kR.ok) setCalRows((await kR.json()) as CalendarIntegration[]);
      setErr(null);
    } catch (e) {
      setErr(describeApiError(e));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const chatConnected = chatRows.some((r) => r.enabled);
  const calConnected = calRows.some((r) => r.enabled);
  const connectedOf = (k: AppKey) => (k === 'chat' ? chatConnected : calConnected);

  const listItem = (k: AppKey) => (
    <button
      key={k}
      type="button"
      onClick={() => setSelected(k)}
      className={cn(
        'flex w-full items-center gap-2.5 rounded px-3 py-2.5 text-left text-[14px]',
        selected === k ? 'bg-panel-2 text-text font-semibold' : 'text-text-dim hover:bg-panel-hover',
      )}
    >
      {APP_META[k].icon}
      {APP_META[k].title}
    </button>
  );

  const connectedApps = (['chat', 'calendar'] as AppKey[]).filter(connectedOf);
  const otherApps = (['chat', 'calendar'] as AppKey[]).filter((k) => !connectedOf(k));

  if (isLoading) {
    return <AppIntegrationsSkeleton />;
  }

  return (
    <div className="h-full overflow-auto p-6">
      <PageHeader items={[{ label: 'H9' }, { label: '앱 연동' }]} />
      {err && <p className="font-mono text-[12px] text-red mb-3">{err}</p>}

      <div className="flex gap-4 items-start">
        {/* ── 좌: 연동 목록 ── */}
        <aside className="w-[260px] shrink-0 border border-line rounded-md bg-panel">
          <div className="px-4 py-3 border-b border-line text-[15px] font-semibold">연동 목록</div>
          <div className="p-2 flex flex-col gap-1">
            <div className="px-2 pt-2 pb-1 text-[12px] text-text-dim">연동됨</div>
            {connectedApps.length === 0 && (
              <div className="px-2 pb-2 text-[12px] text-text-faint">—</div>
            )}
            {connectedApps.map(listItem)}
            <div className="mt-2 border-t border-line px-2 pt-3 pb-1 text-[12px] text-text-dim">
              연동되지 않음
            </div>
            {otherApps.length === 0 && (
              <div className="px-2 pb-2 text-[12px] text-text-faint">—</div>
            )}
            {otherApps.map(listItem)}
          </div>
        </aside>

        {/* ── 우: 앱 정보 + 연동 정보 ── */}
        <section className="min-w-0 flex-1 flex flex-col gap-4">
          {selected === 'chat' ? (
            <ChatDetail me={me} rows={chatRows} connected={chatConnected} onChanged={load} />
          ) : (
            <CalendarDetail me={me} rows={calRows} connected={calConnected} onChanged={load} />
          )}
        </section>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 공통 조각 — 앱 정보 카드(안내/토글/권한)
// -----------------------------------------------------------------------------
function AppInfoCard({
  app,
  connected,
  busy,
  onToggle,
}: {
  app: AppKey;
  connected: boolean;
  busy: boolean;
  onToggle: () => void;
}): JSX.Element {
  const meta = APP_META[app];
  return (
    <div className="border border-line rounded-md bg-panel">
      <div className="px-5 py-3 border-b border-line text-[15px] font-semibold">{meta.title}</div>
      <div className="px-5 py-4 flex flex-col gap-4">
        <p className="text-[13px] text-text-dim leading-relaxed">{meta.intro}</p>
        <div className="rounded-md border border-line bg-panel-2 px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={connected}
            disabled={busy}
            onClick={onToggle}
            className={cn(
              'relative h-6 w-11 rounded-full transition-colors',
              connected ? 'bg-accent' : 'bg-text-faint',
              busy && 'opacity-50 cursor-not-allowed',
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all',
                connected ? 'left-[22px]' : 'left-0.5',
              )}
            />
          </button>
          <span className="text-[13px] text-text-dim">{connected ? '연동됨' : '연동되지 않음'}</span>
        </div>
        <div>
          <div className="text-[13px] font-semibold text-text mb-2">권한</div>
          <ul className="flex flex-col gap-3">
            {meta.perms.map(([t, d]) => (
              <li key={t}>
                <div className="text-[13px] text-text">· {t}</div>
                <div className="ml-3 text-[12px] text-text-dim">{d}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="py-4">
      <div className="mb-2 text-[13px] font-semibold leading-5 text-text">{label}</div>
      <div className="text-[14px] leading-6 text-text-dim">{children}</div>
    </div>
  );
}

function Chip({ label, onRemove }: { label: string; onRemove?: (() => void) | undefined }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-panel-2 px-3 py-1 text-[13px] text-text">
      {label}
      {onRemove && (
        <button type="button" aria-label="삭제" onClick={onRemove} className="text-text-dim hover:text-text">
          ✕
        </button>
      )}
    </span>
  );
}

// 계정 연결(스코프 확장) 새창 열기.
function openAccountConnect(scopes: string[]): void {
  const returnTo = `${window.location.origin}/#/h9/notices/integrations`;
  const url = `${API_BASE}/api/auth/google/extend?scope=${encodeURIComponent(scopes.join(' '))}&return_to=${encodeURIComponent(returnTo)}`;
  window.open(url, '_blank', 'noopener');
}

// 스페이스 검색 콤보 — available-spaces에서 필터, 선택 시 onPick.
function SpacePicker({
  spaces,
  exclude,
  placeholder,
  disabled,
  onPick,
}: {
  spaces: Space[];
  exclude: Set<string>;
  placeholder: string;
  disabled?: boolean;
  onPick: (s: Space) => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return spaces
      .filter((s) => !exclude.has(s.name))
      .filter((s) => !t || s.displayName.toLowerCase().includes(t))
      .slice(0, 30);
  }, [spaces, exclude, q]);
  return (
    <div className="relative max-w-[360px]">
      <input
        value={q}
        disabled={disabled ?? false}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-panel-2 border border-line rounded text-[14px] text-text"
      />
      {open && !disabled && (
        <div className="absolute z-40 top-full mt-1 left-0 right-0 max-h-[200px] overflow-auto rounded-md border border-line bg-panel shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-center text-[12px] text-text-dimmer">
              선택 가능한 스페이스가 없습니다.
            </div>
          ) : (
            filtered.map((s) => (
              <button
                key={s.name}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(s);
                  setQ('');
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-[14px] hover:bg-panel-2 text-text"
              >
                {s.displayName}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Google Chat 상세
// -----------------------------------------------------------------------------
function ChatDetail({
  me,
  rows,
  connected,
  onChanged,
}: {
  me: AuthMe | null;
  rows: ChatIntegration[];
  connected: boolean;
  onChanged: () => Promise<void> | void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [spacesErr, setSpacesErr] = useState<string | null>(null);
  // 편집 상태 — 수신 스페이스(칩)·승인 스페이스(최대 1).
  const [recv, setRecv] = useState<Space[]>([]);
  const [approval, setApproval] = useState<Space | null>(null);

  const hasRows = rows.length > 0;
  // 미연동 + row 없음 → 최초 설정 모드.
  const setupMode = !hasRows || editing;

  const scopesOk =
    !!me &&
    me.grantedScopes.includes(GOOGLE_SCOPE.chatReadonly) &&
    me.grantedScopes.includes(GOOGLE_SCOPE.chatSpacesReadonly);

  // 스페이스 목록 로드 — 라벨 해석(읽기 뷰)과 검색(설정/수정) 겸용.
  useEffect(() => {
    if (!scopesOk) return;
    setSpacesErr(null);
    fetch(`${API_BASE}/api/integrations/chat/available-spaces`)
      .then(async (r) => {
        if (!r.ok) {
          const body = (await r.json().catch(() => ({}))) as { error?: string; detail?: string };
          throw new Error(body.detail || body.error || `HTTP ${r.status}`);
        }
        return (await r.json()) as Space[];
      })
      .then((list) => setSpaces(Array.isArray(list) ? list : []))
      .catch((e: unknown) =>
        setSpacesErr(e instanceof Error ? e.message : String(e)),
      );
  }, [setupMode, scopesOk]);

  // 검색 대상 = Google 스페이스 목록 + 이미 등록된 수신 스페이스(목록 실패 시에도
  // 기존 스페이스 재선택 가능). 승인 스페이스 칩 라벨도 목록 로드 후 갱신.
  const pickableSpaces = useMemo(() => {
    const merged = new Map<string, Space>();
    for (const r of rows) merged.set(r.spaceId, { name: r.spaceId, displayName: r.label });
    for (const sp of spaces) merged.set(sp.name, sp);
    return [...merged.values()];
  }, [spaces, rows]);

  useEffect(() => {
    if (!approval) return;
    const found = spaces.find((sp) => sp.name === approval.name);
    if (found && found.displayName !== approval.displayName) setApproval(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces]);

  const spaceLabel = useCallback(
    (id: string): string => spaces.find((sp) => sp.name === id)?.displayName ?? id,
    [spaces],
  );

  // 수정 진입 시 기존 값 프리필.
  useEffect(() => {
    if (!editing) return;
    setRecv(rows.map((r) => ({ name: r.spaceId, displayName: r.label })));
    const ap = rows[0]?.approvalSpaceIds[0];
    setApproval(ap ? { name: ap, displayName: spaces.find((s) => s.name === ap)?.displayName ?? ap } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  async function toggle(): Promise<void> {
    if (busy) return;
    if (connected) {
      setDisconnectOpen(true);
      return;
    }
    // OFF→ON: row가 있으면 전부 재활성화, 없으면 설정 모드로.
    if (hasRows) {
      setBusy(true);
      try {
        await Promise.all(
          rows.map((r) =>
            fetch(`${API_BASE}/api/integrations/chat/${r.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ enabled: true }),
            }),
          ),
        );
        toast('연동되었습니다.', 'success');
        await onChanged();
      } catch (e) {
        toast(describeApiError(e), 'error');
      } finally {
        setBusy(false);
      }
    } else if (!scopesOk) {
      openAccountConnect([GOOGLE_SCOPE.chatReadonly, GOOGLE_SCOPE.chatSpacesReadonly]);
    }
    // row 없음 + 스코프 OK → 아래 설정 폼에서 확인.
  }

  async function save(): Promise<void> {
    if (busy || !me) return;
    if (recv.length === 0 || !approval) {
      toast('수신 스페이스와 승인 스페이스를 선택해 주세요.', 'error');
      return;
    }
    setBusy(true);
    try {
      const existingBySpace = new Map(rows.map((r) => [r.spaceId, r]));
      const keep = new Set(recv.map((s) => s.name));
      // 제거된 수신 스페이스 삭제.
      for (const r of rows) {
        if (!keep.has(r.spaceId)) {
          await fetch(`${API_BASE}/api/integrations/chat/${r.id}`, { method: 'DELETE' });
        }
      }
      // 추가/갱신 — 승인 스페이스는 모든 행에 동일 반영.
      for (const s of recv) {
        const existing = existingBySpace.get(s.name);
        if (existing) {
          const pr = await fetch(`${API_BASE}/api/integrations/chat/${existing.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled: true, approvalSpaceIds: [approval.name] }),
          });
          if (!pr.ok) throw new Error(await apiErrorMessage(pr));
        } else {
          const cr = await fetch(`${API_BASE}/api/integrations/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              spaceId: s.name,
              label: s.displayName,
              ownerEmail: me.email,
              approvalSpaceIds: [approval.name],
            }),
          });
          if (!cr.ok && cr.status !== 409) throw new Error(await apiErrorMessage(cr));
        }
      }
      toast(hasRows ? '저장되었습니다.' : '연동되었습니다.', 'success');
      setEditing(false);
      await onChanged();
    } catch (e) {
      toast(describeApiError(e), 'error');
    } finally {
      setBusy(false);
    }
  }

  // 수동 새로고침 — 모든 수신 스페이스 행에 대해 폴링 1회를 즉시 실행하고
  // 목록을 다시 읽어 연동 상태/최근 수신을 갱신한다.
  async function pollNow(): Promise<void> {
    if (busy || rows.length === 0) return;
    setBusy(true);
    try {
      for (const r of rows) {
        const pr = await fetch(`${API_BASE}/api/integrations/chat/${r.id}/test`, {
          method: 'POST',
        });
        if (!pr.ok) throw new Error(await apiErrorMessage(pr));
      }
      await onChanged();
      toast('폴링을 실행했습니다.', 'success');
    } catch (e) {
      toast(describeApiError(e), 'error');
    } finally {
      setBusy(false);
    }
  }

  const lastReceived = rows.reduce<string | null>((acc, r) => {
    const v = r.lastSeenMessageAt ?? r.lastPolledAt;
    return v && (!acc || v > acc) ? v : acc;
  }, null);
  const hasError = rows.some((r) => r.lastError);
  const connectedAt = rows.reduce<string | null>(
    (acc, r) => (!acc || r.createdAt < acc ? r.createdAt : acc),
    null,
  );

  return (
    <>
      <AppInfoCard app="chat" connected={connected} busy={busy} onToggle={() => void toggle()} />

      <div className="border border-line rounded-md bg-panel">
        <div className="flex items-center justify-between px-5 py-3 border-b border-line">
          <span className="text-[15px] font-semibold">연동 정보</span>
          {setupMode ? (
            <div className="flex gap-2">
              {hasRows && (
                <Button onClick={() => setEditing(false)} disabled={busy}>취소</Button>
              )}
              <Button variant="primary" onClick={() => void save()} disabled={busy || recv.length === 0 || !approval}>
                {hasRows ? '저장' : '확인'}
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              {hasRows && (
                <Button onClick={() => void pollNow()} disabled={busy}>지금 폴링</Button>
              )}
              <Button variant="primary" onClick={() => setEditing(true)}>수정</Button>
            </div>
          )}
        </div>

        <div className="px-5 py-4">
          {!hasRows && !scopesOk ? (
            <div className="py-14 text-center text-[14px] text-text-dim">
              연동되지 않았습니다.
              <div className="mt-3">
                <Button variant="primary" onClick={() => openAccountConnect([GOOGLE_SCOPE.chatReadonly, GOOGLE_SCOPE.chatSpacesReadonly])}>
                  계정 연결
                </Button>
              </div>
            </div>
          ) : setupMode ? (
            <div className="flex flex-col gap-5 max-w-[560px]">
              <div>
                <div className="text-[13px] font-semibold text-text mb-1.5">
                  연동 계정 <span className="text-red">*</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    value={me?.email ?? ''}
                    disabled
                    className="flex-1 px-3 py-2 bg-panel-3 border border-line rounded text-[14px] text-text-dim cursor-not-allowed"
                  />
                  <Button onClick={() => openAccountConnect([GOOGLE_SCOPE.chatReadonly, GOOGLE_SCOPE.chatSpacesReadonly])}>
                    계정 변경
                  </Button>
                </div>
              </div>
              <div>
                <div className="text-[13px] font-semibold text-text mb-1">
                  수신 스페이스 <span className="text-red">*</span>
                </div>
                <p className="text-[12px] text-text-dim mb-2">
                  메시지를 읽을 스페이스를 선택합니다. 스페이스를 읽고 공지 초안을 생성하며, 지정된 형식이
                  아닌 경우 생성되지 않습니다.
                </p>
                <SpacePicker
                  spaces={pickableSpaces}
                  exclude={new Set(recv.map((s) => s.name))}
                  placeholder="스페이스명 검색"
                  onPick={(s) => setRecv((prev) => [...prev, s])}
                />
                {spacesErr && (
                  <p className="mt-1.5 text-[12px] text-red">
                    스페이스 목록을 불러오지 못했습니다: {spacesErr} — 계정 변경으로 Google 계정을
                    다시 연결해 주세요.
                  </p>
                )}
                {recv.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {recv.map((s) => (
                      <Chip
                        key={s.name}
                        label={s.displayName}
                        onRemove={() => setRecv((prev) => prev.filter((x) => x.name !== s.name))}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[13px] font-semibold text-text mb-1">
                  승인 스페이스 <span className="text-red">*</span>
                </div>
                <p className="text-[12px] text-text-dim mb-2">
                  공지 초안을 받을 스페이스를 선택합니다. 스페이스는 최대 1개까지 지정할 수 있습니다.
                </p>
                <SpacePicker
                  spaces={pickableSpaces}
                  exclude={new Set(approval ? [approval.name] : [])}
                  placeholder="스페이스명 검색"
                  disabled={!!approval}
                  onPick={setApproval}
                />
                {approval && (
                  <div className="mt-2">
                    <Chip label={approval.displayName} onRemove={() => setApproval(null)} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-line">
              <InfoRow label="연동 계정">{rows[0]?.ownerEmail ?? '-'}</InfoRow>
              <InfoRow label="수신 스페이스">
                <div className="flex flex-wrap gap-2 pt-1">
                  {rows.map((r) => (
                    <Chip key={r.id} label={r.label} />
                  ))}
                </div>
              </InfoRow>
              <InfoRow label="승인 스페이스">
                <div className="flex flex-wrap gap-2 pt-1">
                  {rows[0]?.approvalSpaceIds.length ? (
                    rows[0].approvalSpaceIds.map((id) => <Chip key={id} label={spaceLabel(id)} />)
                  ) : (
                    '-'
                  )}
                </div>
              </InfoRow>
              <InfoRow label="연동 상태">
                {hasError ? '오류' : '정상'} / 최근 수신 {formatDateTime(lastReceived)}
              </InfoRow>
              <InfoRow label="연동일">{formatDateTime(connectedAt)}</InfoRow>
            </div>
          )}
        </div>
      </div>

      {disconnectOpen && (
        <ConfirmModal
          title="알림"
          message={'조직 전체의 연동이 해제되며 기존 데이터는 유지됩니다.\n연동을 해제하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setDisconnectOpen(false)}
          onConfirm={async () => {
            await Promise.all(
              rows.map((r) =>
                fetch(`${API_BASE}/api/integrations/chat/${r.id}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ enabled: false }),
                }),
              ),
            );
            toast('연동이 해제되었습니다.', 'success');
            await onChanged();
          }}
        />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Google Calendar 상세 — 연동 계정의 회의실 목록에서 검색·선택 등록.
// -----------------------------------------------------------------------------
function CalendarDetail({
  me,
  rows,
  connected,
  onChanged,
}: {
  me: AuthMe | null;
  rows: CalendarIntegration[];
  connected: boolean;
  onChanged: () => Promise<void> | void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [rooms, setRooms] = useState<CalendarAvailableRoom[]>([]);
  const [roomsErr, setRoomsErr] = useState<string | null>(null);
  const [selectedRooms, setSelectedRooms] = useState<Space[]>([]);

  const hasRows = rows.length > 0;
  const setupMode = !hasRows || editing;
  const scopesOk = !!me && me.grantedScopes.some((s) => s.includes('calendar'));

  // 회의실 목록 로드 — 검색 콤보(설정/수정)용.
  useEffect(() => {
    if (!scopesOk) return;
    setRoomsErr(null);
    fetch(`${API_BASE}/api/integrations/calendar/available-rooms`)
      .then(async (r) => {
        if (!r.ok) {
          const body = (await r.json().catch(() => ({}))) as { error?: string; detail?: string };
          throw new Error(body.detail || body.error || `HTTP ${r.status}`);
        }
        const parsed = (await r.json()) as CalendarAvailableRoom[] | { rooms?: CalendarAvailableRoom[] };
        return Array.isArray(parsed) ? parsed : parsed.rooms ?? [];
      })
      .then(setRooms)
      .catch((e: unknown) => setRoomsErr(e instanceof Error ? e.message : String(e)));
  }, [scopesOk]);

  // 검색 대상 = Google 회의실 목록 + 이미 등록된 회의실(목록 실패 대비 병합).
  const pickableRooms = useMemo<Space[]>(() => {
    const merged = new Map<string, Space>();
    for (const r of rows) merged.set(r.calendarId, { name: r.calendarId, displayName: r.label });
    for (const room of rooms) merged.set(room.id, { name: room.id, displayName: room.summary ?? room.id });
    return [...merged.values()];
  }, [rooms, rows]);

  // 수정 진입 시 기존 등록 회의실 프리필.
  useEffect(() => {
    if (!editing) return;
    setSelectedRooms(rows.map((r) => ({ name: r.calendarId, displayName: r.label })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  async function toggle(): Promise<void> {
    if (busy) return;
    if (connected) {
      setDisconnectOpen(true);
      return;
    }
    if (hasRows) {
      setBusy(true);
      try {
        await Promise.all(
          rows.map((r) =>
            fetch(`${API_BASE}/api/integrations/calendar/${r.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ enabled: true }),
            }),
          ),
        );
        toast('연동되었습니다.', 'success');
        await onChanged();
      } catch (e) {
        toast(describeApiError(e), 'error');
      } finally {
        setBusy(false);
      }
    } else if (!scopesOk) {
      openAccountConnect([GOOGLE_SCOPE.calendarListReadonly, GOOGLE_SCOPE.calendarEventsReadonly]);
    }
  }

  // 확인/저장 — 선택된 회의실 목록으로 rows를 동기화(추가 POST / 제거 DELETE).
  async function save(): Promise<void> {
    if (busy || !me) return;
    if (selectedRooms.length === 0) {
      toast('등록할 회의실을 선택해 주세요.', 'error');
      return;
    }
    setBusy(true);
    try {
      const existingByCal = new Map(rows.map((r) => [r.calendarId, r]));
      const keep = new Set(selectedRooms.map((s) => s.name));
      for (const r of rows) {
        if (!keep.has(r.calendarId)) {
          await fetch(`${API_BASE}/api/integrations/calendar/${r.id}`, { method: 'DELETE' });
        }
      }
      for (const s of selectedRooms) {
        if (existingByCal.has(s.name)) continue;
        const cr = await fetch(`${API_BASE}/api/integrations/calendar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ calendarId: s.name, label: s.displayName, ownerEmail: me.email }),
        });
        if (!cr.ok && cr.status !== 409) throw new Error(`HTTP ${cr.status}`);
      }
      toast(hasRows ? '저장되었습니다.' : '연동되었습니다.', 'success');
      setEditing(false);
      await onChanged();
    } catch (e) {
      toast(describeApiError(e), 'error');
    } finally {
      setBusy(false);
    }
  }

  // 수동 새로고침 — 등록된 모든 회의실 행에 대해 폴링 1회를 즉시 실행하고
  // 목록을 다시 읽어 연동 상태/최근 수신을 갱신한다.
  async function pollNow(): Promise<void> {
    if (busy || rows.length === 0) return;
    setBusy(true);
    try {
      for (const r of rows) {
        const pr = await fetch(`${API_BASE}/api/integrations/calendar/${r.id}/test`, {
          method: 'POST',
        });
        if (!pr.ok) throw new Error(`HTTP ${pr.status}`);
      }
      await onChanged();
      toast('폴링을 실행했습니다.', 'success');
    } catch (e) {
      toast(describeApiError(e), 'error');
    } finally {
      setBusy(false);
    }
  }

  const lastReceived = rows.reduce<string | null>((acc, r) => {
    const v = r.lastPolledAt;
    return v && (!acc || v > acc) ? v : acc;
  }, null);
  const connectedAt = rows.reduce<string | null>(
    (acc, r) => (!acc || r.createdAt < acc ? r.createdAt : acc),
    null,
  );

  return (
    <>
      <AppInfoCard app="calendar" connected={connected} busy={busy} onToggle={() => void toggle()} />

      <div className="border border-line rounded-md bg-panel">
        <div className="flex items-center justify-between px-5 py-3 border-b border-line">
          <span className="text-[15px] font-semibold">연동 정보</span>
          {setupMode ? (
            scopesOk && (
              <div className="flex gap-2">
                {hasRows && <Button onClick={() => setEditing(false)} disabled={busy}>취소</Button>}
                <Button
                  variant="primary"
                  onClick={() => void save()}
                  disabled={busy || selectedRooms.length === 0}
                >
                  {hasRows ? '저장' : '확인'}
                </Button>
              </div>
            )
          ) : (
            <div className="flex gap-2">
              {hasRows && (
                <Button onClick={() => void pollNow()} disabled={busy}>지금 폴링</Button>
              )}
              <Button variant="primary" onClick={() => setEditing(true)}>수정</Button>
            </div>
          )}
        </div>
        <div className="px-5 py-4">
          {!hasRows && !scopesOk ? (
            <div className="py-14 text-center text-[14px] text-text-dim">
              연동되지 않았습니다.
              <div className="mt-3">
                <Button
                  variant="primary"
                  onClick={() => openAccountConnect([GOOGLE_SCOPE.calendarListReadonly, GOOGLE_SCOPE.calendarEventsReadonly])}
                >
                  계정 연결
                </Button>
              </div>
            </div>
          ) : setupMode ? (
            <div className="flex flex-col gap-5 max-w-[560px]">
              <div>
                <div className="text-[13px] font-semibold text-text mb-1.5">
                  연동 계정 <span className="text-red">*</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    value={me?.email ?? ''}
                    disabled
                    className="flex-1 px-3 py-2 bg-panel-3 border border-line rounded text-[14px] text-text-dim cursor-not-allowed"
                  />
                  <Button onClick={() => openAccountConnect([GOOGLE_SCOPE.calendarListReadonly, GOOGLE_SCOPE.calendarEventsReadonly])}>
                    계정 변경
                  </Button>
                </div>
              </div>
              <div>
                <div className="text-[13px] font-semibold text-text mb-1">
                  회의실 <span className="text-red">*</span>
                </div>
                <p className="text-[12px] text-text-dim mb-2">
                  일정을 표시할 회의실 캘린더를 선택합니다. 등록한 회의실 명의 템플릿이
                  [미디어 관리 &gt; 라이브러리]에 생성됩니다.
                </p>
                <SpacePicker
                  spaces={pickableRooms}
                  exclude={new Set(selectedRooms.map((s) => s.name))}
                  placeholder="회의실명 검색"
                  onPick={(s) => setSelectedRooms((prev) => [...prev, s])}
                />
                {roomsErr && (
                  <p className="mt-1.5 text-[12px] text-red">
                    회의실 목록을 불러오지 못했습니다: {roomsErr} — 계정 변경으로 Google 계정을
                    다시 연결해 주세요.
                  </p>
                )}
                {selectedRooms.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedRooms.map((s) => (
                      <Chip
                        key={s.name}
                        label={s.displayName}
                        onRemove={() =>
                          setSelectedRooms((prev) => prev.filter((x) => x.name !== s.name))
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-line">
              <InfoRow label="연동 계정">{rows[0]?.ownerEmail ?? '-'}</InfoRow>
              <InfoRow label="회의실">
                <div className="flex flex-wrap gap-2 pt-1">
                  {rows.map((r) => (
                    <Chip key={r.id} label={r.label} />
                  ))}
                </div>
              </InfoRow>
              <InfoRow label="연동 상태">정상 / 최근 수신 {formatDateTime(lastReceived)}</InfoRow>
              <InfoRow label="연동일">{formatDateTime(connectedAt)}</InfoRow>
            </div>
          )}
        </div>
      </div>

      {disconnectOpen && (
        <ConfirmModal
          title="알림"
          message={'조직 전체의 연동이 해제되며 기존 데이터는 유지됩니다.\n연동을 해제하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setDisconnectOpen(false)}
          onConfirm={async () => {
            await Promise.all(
              rows.map((r) =>
                fetch(`${API_BASE}/api/integrations/calendar/${r.id}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ enabled: false }),
                }),
              ),
            );
            toast('연동이 해제되었습니다.', 'success');
            await onChanged();
          }}
        />
      )}
    </>
  );
}

function ChatIcon(): JSX.Element {
  return <img src={googleChatIcon} width={18} height={18} alt="" aria-hidden="true" />;
}

function CalendarIcon(): JSX.Element {
  return <img src={googleCalendarIcon} width={18} height={18} alt="" aria-hidden="true" />;
}
